import { z } from 'zod';

import { MAX_ITEM_BARCODES } from '../enums.js';

import { ItemUnitInputSchema, ItemUnitSchema } from './itemUnit.js';
import { ItemVariantInputSchema, ItemVariantSchema } from './itemVariant.js';

export const ItemTypeSchema = z.enum(['product', 'service']);
export type ItemType = z.infer<typeof ItemTypeSchema>;

// Symbology for the stored barcode value. Code-128 is the default —
// it encodes arbitrary ASCII, so it handles SKU-shaped strings without
// the digit-only restrictions of EAN/UPC.
export const BarcodeFormatSchema = z.enum(['code128', 'ean13', 'ean8', 'upc_a', 'qr']);
export type BarcodeFormat = z.infer<typeof BarcodeFormatSchema>;

export const ItemSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  description: z.string().nullable(),
  itemType: ItemTypeSchema,
  isSellable: z.boolean(),
  isPurchasable: z.boolean(),
  isInventoryTracked: z.boolean(),
  /** Informational "raw material" flag (manufacturing module) — the item stays
   *  sellable and purchasable; this only powers a list filter. */
  isRawMaterial: z.boolean(),
  /** This item is a COMBO — a fixed bundle sold at one price, whose components
   *  come off the shelf when it sells (combos module). It keeps no stock of its
   *  own; what is inside lives in combo_lines. */
  isCombo: z.boolean(),
  /** Whether sales of this product earn customer cashback (default true). */
  earnsCashback: z.boolean(),
  unitOfMeasure: z.string().max(16),
  salePrice: z
    .string()
    .regex(/^-?\d+(\.\d{1,4})?$/)
    .nullable(),
  saleCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable(),
  purchasePrice: z
    .string()
    .regex(/^-?\d+(\.\d{1,4})?$/)
    .nullable(),
  purchaseCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable(),
  taxRate: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable(),
  barcode: z.string().max(64).nullable(),
  barcodeFormat: BarcodeFormatSchema,
  /**
   * Extra scannable codes that ALSO resolve to this item — for a product sold
   * under more than one manufacturer label (a colour/print-run change, a
   * re-issued EAN). Resolution-only: `barcode` above stays the primary and is
   * the one that gets printed. Never contains the primary itself.
   *
   * THE BASE UNIT'S codes only. A code that belongs on a pack or a carton lives
   * on that unit (`ItemUnit.barcodes`) so a scan charges for the pack; listing
   * it here would sell one piece instead of eight hundred.
   */
  barcodes: z.array(z.string().max(64)),
  categoryId: z.string().uuid().nullable(),
  /**
   * EXTRA categories this product ALSO shows under (the `multi_category`
   * module). `categoryId` above stays the primary and keeps its meaning
   * everywhere; these are additional memberships and never contain it.
   * Optional: an older API omits the field entirely.
   */
  extraCategoryIds: z.array(z.string().uuid()).optional(),
  /** Product brand/manufacturer (see BrandSchema). */
  brandId: z.string().uuid().nullable(),
  /** User-defined custom field values, keyed by field code (see CustomFieldDefinition). */
  customFields: z.record(z.unknown()).nullable(),
  /** Optional product video (e.g. a YouTube link) shown on the storefront/app. */
  videoUrl: z.string().nullable(),
  /** Hide this item from the customer-facing app/storefront ("اخفاء في التطبيق"). */
  isHidden: z.boolean(),
  /** Highlight this item as featured. */
  isFeatured: z.boolean(),
  /**
   * Listed on the public store and the customer app ("نشر").
   *
   * NOT a column on `items` — it lives on the `store_products` overlay, which
   * is why it reads false for every product nobody has published yet: absent
   * overlay row means absent from the shop. Writing it through the item create
   * and update paths is what lets an operator publish without a second trip to
   * the CMS; /cms/products edits the same flag.
   */
  isPublished: z.boolean(),
  /**
   * Additional selling units (pack/case); the base unit is the item itself.
   * Only units that are ON SALE — a hidden unit (isActive false) appears only
   * where the caller asked for hidden units (the item form and detail page).
   */
  units: z.array(ItemUnitSchema),
  /** Selectable variants/options (e.g. Color, Size), each with own price/stock/barcode. */
  variants: z.array(ItemVariantSchema),
  isActive: z.boolean(),
  /** Storefront views (denormalized counter). Present only on list({ metrics }). */
  viewCount: z.number().optional(),
  /** Units sold across POS + online orders. Present only on list({ metrics }). */
  soldCount: z.number().optional(),
  /**
   * Total on hand across every warehouse, as a decimal string. Present only on
   * list({ stock }) — it costs an extra aggregation, so the item list asks for
   * it only when the operator turns the column on. A service or an untracked
   * product has no shelf: it comes back '0' and should read as "—".
   */
  onHand: z.string().optional(),
  /**
   * The same number SPLIT BY WAREHOUSE — every active warehouse, including the
   * ones holding none of it, so "where is it?" has a complete answer rather
   * than a list of the places that happen to have a row. Present only on
   * list({ stockDetail }) with explicit ids: it is a lookup for ONE product
   * (the register's detail card), never a column over the whole catalogue.
   */
  onHandByWarehouse: z
    .array(z.object({
      warehouseId: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      quantity: z.string(),
    }))
    .optional(),
  /**
   * Attachment ids of this product's pictures, oldest first — so `[0]` is the
   * thumbnail every client already treats as the main image. Present only on
   * list({ images }).
   *
   * It exists for the offline apps: they cache picture BYTES on the device and
   * need to know which ones changed without fetching each one to find out.
   * Comparing the first id against what is cached turns "re-download 9,000
   * images" into "download the four that were replaced".
   */
  imageIds: z.array(z.string().uuid()).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Item = z.infer<typeof ItemSchema>;

/** A document line that referenced this item — for the item's sales/purchase tabs. */
export interface ItemTxnRow {
  docType: 'invoice' | 'salesOrder' | 'bill' | 'purchaseOrder';
  docId: string;
  docNumber: string;
  docDate: string;
  partyName: string;
  status: string;
  quantity: string;
  unitName: string | null;
  unitPrice: string;
  lineTotal: string;
  currencyCode: string;
  /** Base units per line unit (1 for the base unit) — quantity × factor is the stock effect. */
  unitFactor?: string;
  /** A register sale's invoice (sales side only). */
  isPos?: boolean;
  /** A debit note — goods sent BACK to the supplier (purchase side only). */
  isReturn?: boolean;
  /** The branch the document belongs to, when it names one. */
  warehouseName?: string | null;
}

/**
 * Who may see what an item COSTS — its average cost, stock value, purchase
 * prices. Any one of these: buying it (bills), answering for what the shelves
 * are worth (the stock report), or reading margins (profits). Stock itself is
 * stock.read, which every cashier holds to look at a shelf; the money behind it
 * is a separate question. Shared so the API and the page agree.
 */
export const ITEM_COST_PERMISSIONS = ['bills.read', 'stock.report', 'profits.read', 'profits.report'] as const;

/** A money total in one currency. */
export interface ItemMoneyTotal {
  currencyCode: string;
  amount: string;
}

/**
 * The item profile's headline figures — sold, returned, last sale, last
 * purchase, best customers.
 *
 * "Sold" here is POSTED INVOICE LINES (issued or paid, credit notes excluded):
 * every channel ends on one — the register writes one per sale, the office and
 * the reps write them directly — so it is the one basis a wholesaler's counter
 * and office sales both land on. Quantities are BASE units (quantity × the
 * line's unit factor), so cartons and pieces add up. Branch-scoped like the
 * documents themselves.
 */
/**
 * What a purchase-bill line shows about its product (GET /items/purchase-context):
 * the price it was last bought at — the old system's «آخر سعر» — and the prices
 * it sells at, so a changed cost can be seen and the selling price followed up.
 */
export interface ItemPurchaseContext {
  itemId: string;
  /** The latest posted bill line (the bill being edited excluded); null = never bought. */
  lastPurchase: {
    docNumber: string;
    docDate: string;
    partyName: string | null;
    unitName: string | null;
    unitFactor: string;
    unitPrice: string;
    /** One BASE unit, net of tax — compare any unit by multiplying with its factor. */
    perBase: string;
    currencyCode: string;
  } | null;
  /** The product's own selling price (retail), base unit and per pack unit id. */
  retail: { currencyCode: string | null; base: string | null; units: Record<string, string | null> };
  /** Every active price list with an explicit price for it (e.g. «جملة»). */
  lists: Array<{ id: string; name: string; currencyCode: string; base: string | null; units: Record<string, string> }>;
}

export interface ItemInsights {
  sold: {
    /** Window lengths in days, ending today. */
    shortDays: number;
    longDays: number;
    shortUnits: string;
    longUnits: string;
    shortInvoices: number;
    allTimeUnits: string;
    shortRevenue: ItemMoneyTotal[];
  };
  lastSale: {
    invoiceId: string;
    docNumber: string;
    docDate: string;
    isPos: boolean;
    partyName: string | null;
    quantity: string;
    unitName: string | null;
    unitPrice: string;
    currencyCode: string;
  } | null;
  returns: { units: string; creditNotes: number };
  /** Null when the viewer may not see costs (ITEM_COST_PERMISSIONS) or nothing was ever bought. */
  lastPurchase: {
    billId: string;
    docNumber: string;
    docDate: string;
    partyName: string | null;
    quantity: string;
    unitName: string | null;
    unitPrice: string;
    /** Net (ex-tax) cost of ONE base unit on that bill. */
    baseUnitCost: string;
    currencyCode: string;
  } | null;
  /** Best customers of this item by base units bought, all time. */
  topCustomers: Array<{
    partyId: string | null;
    partyName: string | null;
    units: string;
    invoices: number;
    lastDate: string;
    revenue: ItemMoneyTotal[];
  }>;
}

/** One credit-note line that returned this item. */
export interface ItemReturnRow {
  creditNoteId: string;
  docNumber: string;
  docDate: string;
  status: string;
  parentInvoiceId: string | null;
  parentDocNumber: string | null;
  partyName: string | null;
  warehouseName: string | null;
  quantity: string;
  unitName: string | null;
  unitFactor: string;
  unitPrice: string;
  lineTotal: string;
  currencyCode: string;
  reason: string | null;
}

/** A currency-scoped money total (revenue can span currencies). */
export interface ItemReportMoney {
  currencyCode: string;
  amount: string;
}

/** One sales channel's contribution to an item's report. */
export interface ItemReportChannel {
  units: string;
  salesCount: number;
  revenue: ItemReportMoney[];
}

/**
 * Aggregated sales report for a single item — units sold, revenue, a channel
 * split, and a monthly units trend. "Sold" is on the SAME basis as the inventory
 * list's Sold column: POS finalized/partially-refunded sales + online sales
 * orders (never invoice_lines, which would double-count POS).
 */
export interface ItemReport {
  views: number;
  totalUnits: string;
  salesCount: number;
  revenue: ItemReportMoney[];
  pos: ItemReportChannel;
  online: ItemReportChannel;
  /** Units sold per calendar month (YYYY-MM), chronological, months with sales only. */
  monthly: Array<{ month: string; units: string }>;
  firstSoldAt: string | null;
  lastSoldAt: string | null;
}

export const ItemCreateSchema = z.object({
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  description: z.string().max(20000).optional(),
  itemType: ItemTypeSchema.default('product'),
  isSellable: z.boolean().default(true),
  isPurchasable: z.boolean().default(true),
  /**
   * Left out = a PRODUCT is stock-tracked, a service is not (decided in
   * ItemsService.create). It used to default to false, and an untracked product
   * is never taken off the shelf by a sale: Duhok's new products all sold
   * without their stock moving (2026-10-25).
   */
  isInventoryTracked: z.boolean().optional(),
  // Optional (not defaulted): a default would make the INFERRED type require it
  // and break every internal caller that builds an ItemCreate in TS.
  isRawMaterial: z.boolean().optional(),
  isCombo: z.boolean().optional(),
  earnsCashback: z.boolean().optional(),
  unitOfMeasure: z.string().min(1).max(16).default('ea'),
  salePrice: z
    .string()
    .regex(/^-?\d+(\.\d{1,4})?$/)
    .optional(),
  saleCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).optional(),
  purchasePrice: z
    .string()
    .regex(/^-?\d+(\.\d{1,4})?$/)
    .optional(),
  purchaseCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).optional(),
  taxRate: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
  barcode: z.string().max(64).optional(),
  barcodeFormat: BarcodeFormatSchema.optional(),
  /** Extra scannable codes that also resolve to this item (see ItemSchema.barcodes). */
  barcodes: z.array(z.string().max(64)).max(MAX_ITEM_BARCODES).optional(),
  categoryId: z.string().uuid().optional(),
  /** Extra category memberships (multi_category module); primary excluded. */
  extraCategoryIds: z.array(z.string().uuid()).max(10).optional(),
  brandId: z.string().uuid().optional(),
  customFields: z.record(z.unknown()).optional(),
  videoUrl: z.string().max(500).optional(),
  isHidden: z.boolean().optional(),
  isFeatured: z.boolean().optional(),
  /**
   * Put it on the store/app straight away (creates the `store_products` row).
   *
   * Deliberately NOT defaulted: the forms tick the box for the operator, but a
   * programmatic create that says nothing must keep the old behaviour of
   * landing unpublished — the Excel importer counts on it.
   */
  isPublished: z.boolean().optional(),
  units: z.array(ItemUnitInputSchema).max(20).optional(),
  /**
   * `units` above is the COMPLETE set, hidden ones included: anything missing
   * from it is removed. Only a client that shows hidden units (the item form)
   * may say so. Without it the list is read as "the units I can see" and a
   * hidden unit that is not in it is KEPT — an older client (the manager exe,
   * the importers) never sees hidden units, and its next save must not delete
   * them. Also makes the response carry hidden units, so the caller can map
   * its rows onto the saved ids by position.
   */
  includesHiddenUnits: z.boolean().optional(),
  variants: z.array(ItemVariantInputSchema).max(100).optional(),
});

export type ItemCreate = z.infer<typeof ItemCreateSchema>;

export const ItemUpdateSchema = z.object({
  code: z.string().min(1).max(64).optional(),
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(20000).nullable().optional(),
  isSellable: z.boolean().optional(),
  isPurchasable: z.boolean().optional(),
  isInventoryTracked: z.boolean().optional(),
  // In the UPDATE schema too — this schema strips unknown keys, so omitting it
  // here would make the flag create-time-only forever (the item_type trap).
  isRawMaterial: z.boolean().optional(),
  isCombo: z.boolean().optional(),
  earnsCashback: z.boolean().optional(),
  unitOfMeasure: z.string().min(1).max(16).optional(),
  salePrice: z
    .string()
    .regex(/^-?\d+(\.\d{1,4})?$/)
    .nullable()
    .optional(),
  saleCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable().optional(),
  purchasePrice: z
    .string()
    .regex(/^-?\d+(\.\d{1,4})?$/)
    .nullable()
    .optional(),
  purchaseCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable().optional(),
  taxRate: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable().optional(),
  barcode: z.string().max(64).nullable().optional(),
  barcodeFormat: BarcodeFormatSchema.optional(),
  /** Extra scannable codes (see ItemSchema.barcodes). Omit to leave untouched; [] clears. */
  barcodes: z.array(z.string().max(64)).max(MAX_ITEM_BARCODES).optional(),
  categoryId: z.string().uuid().nullable().optional(),
  /** Omit to leave untouched; [] clears — the item_barcodes contract. */
  extraCategoryIds: z.array(z.string().uuid()).max(10).optional(),
  brandId: z.string().uuid().nullable().optional(),
  customFields: z.record(z.unknown()).nullable().optional(),
  videoUrl: z.string().max(500).nullable().optional(),
  isHidden: z.boolean().optional(),
  isFeatured: z.boolean().optional(),
  /** Publish/unpublish on the store and app. Omit to leave untouched. */
  isPublished: z.boolean().optional(),
  units: z.array(ItemUnitInputSchema).max(20).optional(),
  /**
   * `units` above is the COMPLETE set, hidden ones included: anything missing
   * from it is removed. Only a client that shows hidden units (the item form)
   * may say so. Without it the list is read as "the units I can see" and a
   * hidden unit that is not in it is KEPT — an older client (the manager exe,
   * the importers) never sees hidden units, and its next save must not delete
   * them. Also makes the response carry hidden units, so the caller can map
   * its rows onto the saved ids by position.
   */
  includesHiddenUnits: z.boolean().optional(),
  variants: z.array(ItemVariantInputSchema).max(100).optional(),
  isActive: z.boolean().optional(),
});

export type ItemUpdate = z.infer<typeof ItemUpdateSchema>;

/**
 * Counts of records still referencing an item — shown in the delete confirmation
 * and used to decide whether a hard-delete is allowed. `blocking` counts (document
 * lines, stock history) prevent the delete; `owned` counts are removed with it.
 */
export const ItemDeleteCountsSchema = z.object({
  // BLOCKING — historical/transactional; any > 0 refuses the hard-delete.
  invoiceLines: z.number().int().nonnegative(),
  billLines: z.number().int().nonnegative(),
  salesOrderLines: z.number().int().nonnegative(),
  purchaseOrderLines: z.number().int().nonnegative(),
  quotationLines: z.number().int().nonnegative(),
  posSaleLines: z.number().int().nonnegative(),
  stockTransferLines: z.number().int().nonnegative(),
  stockMovements: z.number().int().nonnegative(),
  stocktakeLines: z.number().int().nonnegative(),
  /** Production history referencing the item — blocks the delete. */
  productionOrders: z.number().int().nonnegative(),
  productionOrderLines: z.number().int().nonnegative(),
  /** Recipes: the item's own recipe, and recipes that use it as a component.
   *  Both BLOCK — auto-purging a component out of someone's recipe would be a
   *  silent recipe change; delete or edit the recipe first. */
  bomHeaders: z.number().int().nonnegative(),
  bomComponentLines: z.number().int().nonnegative(),
  /** Combos that sell this item as part of a bundle. BLOCKS for the same
   *  reason a recipe does: pulling a component out from under a live bundle
   *  would silently change what the shop is selling. */
  comboComponentLines: z.number().int().nonnegative(),
  // OWNED — removed together with the item.
  /** The item's OWN component list, when it is itself a combo. */
  comboOwnLines: z.number().int().nonnegative(),
  units: z.number().int().nonnegative(),
  variants: z.number().int().nonnegative(),
  batches: z.number().int().nonnegative(),
  storeProduct: z.number().int().nonnegative(),
  priceListItems: z.number().int().nonnegative(),
  reviews: z.number().int().nonnegative(),
  storyProducts: z.number().int().nonnegative(),
  couponProducts: z.number().int().nonnegative(),
  stockLevels: z.number().int().nonnegative(),
  attachments: z.number().int().nonnegative(),
  translations: z.number().int().nonnegative(),
});
export type ItemDeleteCounts = z.infer<typeof ItemDeleteCountsSchema>;

export const ItemDeletePreviewSchema = z.object({
  itemId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  itemType: ItemTypeSchema,
  isActive: z.boolean(),
  /** True when at least one BLOCKING count is > 0 (delete refused server-side). */
  blocked: z.boolean(),
  counts: ItemDeleteCountsSchema,
});
export type ItemDeletePreview = z.infer<typeof ItemDeletePreviewSchema>;

/**
 * Set the barcode symbology across the whole catalogue in one call.
 *
 * `onlyValid` defaults to TRUE — the safe half of the operation. It skips any product
 * whose existing barcode would not survive the change (see isBarcodeValid): the format
 * is only metadata, so a code the symbology can't carry doesn't break, it just makes
 * the label fall back to Code 128 while claiming to be something else. Passing false is
 * a deliberate "label them all anyway".
 */
export const BulkBarcodeFormatSchema = z.object({
  format: BarcodeFormatSchema,
  onlyValid: z.boolean().default(true),
  /** Count what WOULD change and write nothing — what the confirm dialog opens with. */
  dryRun: z.boolean().default(false),
});
export type BulkBarcodeFormat = z.infer<typeof BulkBarcodeFormatSchema>;

export const BulkBarcodeFormatResultSchema = z.object({
  /** Live products in the catalogue. */
  total: z.number().int().nonnegative(),
  /** …of which these would be (or were) set to the new format. */
  matched: z.number().int().nonnegative(),
  /** …and these were left alone because their barcode can't be that symbology. */
  skipped: z.number().int().nonnegative(),
  /** Rows actually written — 0 for a dry run. */
  updated: z.number().int().nonnegative(),
  /**
   * The breakdown of WHY, counted over the whole catalogue regardless of `onlyValid`,
   * because the two are not the same problem (see barcodeFit): a `renumbered` product
   * would print a barcode that is not its own and scan to nothing, an `unsupported`
   * one just falls back to Code 128 with its number intact.
   */
  renumbered: z.number().int().nonnegative(),
  unsupported: z.number().int().nonnegative(),
});
export type BulkBarcodeFormatResult = z.infer<typeof BulkBarcodeFormatResultSchema>;
